'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button, Skeleton } from '@mokoboko/components';

import { GiftCard } from './gift-card';
import { useDetailHref } from '@/contexts/embed';
import { GIFT_CARD_ASPECT } from '@/lib/gift-card-design';
import { CARD_TITLE_LINK_CLASS } from '@/lib/prose';
import { formatPrice, mergeClassNames } from '@/lib/utils';
import { giftCardValue } from '@/helpers/gift-card';
import type { GiftCard as GiftCardProduct } from '@/state/gift-cards';

// A bordered row with the name and price, the button on their line, the
// studio's short description (the admin promises it shows in the list) under
// both, and the card face beside the row on the right from md. From md the
// name keeps to one line and the description to two, so the tallest row is
// 154px; the face is that tall (154px times the card ratio is 244px wide) and a
// shorter row stretches to it with its content centered. On a phone it all
// stacks: the face sits above the full-width button, where a pack shows its
// image. The face is hidden from assistive tech, so the name is what names the
// card.
const ROW_CLASS =
  'grid w-full grid-cols-1 gap-5 rounded-xl border border-gray-200 bg-white p-4 shadow-xs md:grid-cols-[minmax(0,1fr)_auto] md:content-center md:items-center md:gap-6';

const SIDE_FACE_CLASS = 'hidden w-[244px] shrink-0 md:block';

export const GiftCardTile = ({
  giftCard,
  studioName,
}: {
  giftCard: GiftCardProduct;
  studioName: string;
}) => {
  const router = useRouter();
  const detailHref = useDetailHref(
    'gift_card',
    giftCard.gift_card_id,
    giftCard.slug
  );
  const openCard = () => router.push(detailHref);
  const value = giftCardValue(giftCard);
  const face = (
    <GiftCard
      studioName={studioName}
      look={giftCard.look}
      headline={value}
      compact
      flat
    />
  );

  return (
    <div className="flex w-full flex-row gap-4 md:gap-5">
      <div
        className={mergeClassNames(
          'group cursor-pointer transition-shadow duration-200 md:hover:shadow-lg',
          ROW_CLASS
        )}
        onClick={openCard}
      >
        <div className="flex min-w-0 flex-col gap-2">
          <Link
            href={detailHref}
            className={mergeClassNames(
              CARD_TITLE_LINK_CLASS,
              'group-hover:text-red-500 md:truncate'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {giftCard.name}
          </Link>
          <p className="text-sm text-gray-700">
            {formatPrice(giftCard.price, giftCard.currency)}
          </p>
        </div>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            openCard();
          }}
          className="group-hover:bg-red-500 max-md:order-last max-md:w-full"
        >
          Buy gift card
        </Button>

        <p className="text-sm text-gray-700 md:col-span-2 md:line-clamp-2">
          {giftCard.short_description}
        </p>

        <div aria-hidden className="md:hidden">
          {face}
        </div>
      </div>

      <div aria-hidden className={SIDE_FACE_CLASS}>
        {face}
      </div>
    </div>
  );
};

export const GiftCardTileSkeleton = () => (
  <div className="flex w-full flex-row gap-4 md:gap-5">
    <div className={ROW_CLASS}>
      <div className="flex flex-1 flex-col gap-2">
        <Skeleton text="body-lg" width="60%" />
        <Skeleton text="body-sm" width={48} />
        <Skeleton text="body-sm" width="80%" />
      </div>
    </div>
    <Skeleton
      isRounded
      className={mergeClassNames('!h-auto !rounded-xl', SIDE_FACE_CLASS)}
      style={{ aspectRatio: GIFT_CARD_ASPECT }}
    />
  </div>
);
